// The invoice as a picture, for customers who'd rather get an image in a
// text than open a link.
//
// Drawn from the same InvoiceDocument the customer's page renders, so the
// picture can't drift from the real thing.
import { toBlob } from 'html-to-image';

/**
 * Render an element to a PNG.
 *
 * `cacheBust` matters: the proof image and payment logos are already in
 * the browser cache from plain <img> loads, without CORS headers, and
 * reusing those copies taints the canvas. Asking afresh gets a response
 * the S3 CORS rule covers.
 *
 * Safari draws images and webfonts only on the second pass, so the first
 * is thrown away.
 *
 * An image that still can't be fetched is drawn as a transparent pixel:
 * left to itself, html-to-image swaps in an empty src, which fails to
 * load and takes the whole render down with it. A missing logo is a
 * better outcome than no invoice.
 */
export const BLANK_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

export const renderInvoiceImage = async (node: HTMLElement): Promise<Blob> => {
  const options = {
    pixelRatio: 2,
    backgroundColor: '#ffffff',
    cacheBust: true,
    imagePlaceholder: BLANK_PIXEL,
  };
  await toBlob(node, options);
  const blob = await toBlob(node, options);
  if (!blob) throw new Error('Could not draw the invoice');
  return blob;
};

/** Below the invoice's `wide:` breakpoint, where it lays out for a phone. */
export const isNarrowScreen = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 639px)').matches;

/** Whether this device can hand an image file to the share sheet. */
export const canShareImage = (): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') {
    return false;
  }
  const probe = new File([new Blob()], 'invoice.png', { type: 'image/png' });
  return navigator.canShare({ files: [probe] });
};

/**
 * Put an image on the clipboard.
 *
 * Takes a promise rather than a blob: Safari only allows a clipboard
 * write started inside the click itself, so the ClipboardItem has to be
 * created before the image has finished drawing.
 */
export const copyImage = async (blob: Promise<Blob>): Promise<void> => {
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
};

export type ImageShareOutcome = 'shared' | 'dismissed';

/** Open the share sheet with the image attached. Throws if it can't. */
export const shareImage = async (blob: Blob, filename: string): Promise<ImageShareOutcome> => {
  const file = new File([blob], filename, { type: 'image/png' });
  try {
    await navigator.share({ files: [file] });
    return 'shared';
  } catch (e: any) {
    if (e?.name === 'AbortError') return 'dismissed';
    throw e;
  }
};

/** Save the image to the device, for when sharing and copying aren't available. */
export const downloadImage = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
